import {
  Apple,
  Carrot,
  Egg,
  Milk,
  Cookie,
  ChefHat,
  Wheat,
  Flower,
  Sparkles,
  Sprout,
  Leaf,
  Beef,
  Fish,
  Coffee,
  Package,
  type LucideIcon,
} from "lucide-react";

const CATEGORY_META: Record<string, { label: string; icon: LucideIcon }> = {
  produce: { label: "Produce", icon: Carrot },
  fruit: { label: "Fruit", icon: Apple },
  fruits: { label: "Fruits", icon: Apple },
  vegetables: { label: "Vegetables", icon: Carrot },
  veggies: { label: "Veggies", icon: Carrot },
  greens: { label: "Greens", icon: Leaf },
  herbs: { label: "Herbs", icon: Leaf },
  eggs: { label: "Eggs & Dairy", icon: Egg },
  dairy: { label: "Dairy", icon: Milk },
  meat: { label: "Meat", icon: Beef },
  fish: { label: "Fish", icon: Fish },
  baked: { label: "Baked", icon: Cookie },
  prepared: { label: "Prepared", icon: ChefHat },
  pantry: { label: "Pantry", icon: Wheat },
  drinks: { label: "Drinks", icon: Coffee },
  flowers: { label: "Flowers", icon: Flower },
  homemade: { label: "Homemade", icon: Sparkles },
  garden: { label: "Garden", icon: Sprout },
  other: { label: "Other", icon: Package },
};

// Canonical browse categories, shown identically on every surface (home feed,
// listings browse) so the chip row never depends on which items happen to be
// loaded/zip-filtered. Mirrors the seller form's CATEGORY_OPTIONS (minus the
// "other" catch-all, which is reachable via "All").
export const BROWSE_CATEGORIES = [
  "produce",
  "baked",
  "eggs",
  "meat",
  "garden",
  "flowers",
  "pantry",
  "prepared",
  "homemade",
];

function lookupMeta(category: string): { label: string; icon: LucideIcon } {
  const key = category.trim().toLowerCase();
  if (CATEGORY_META[key]) return CATEGORY_META[key];
  const label = category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  return { label, icon: Package };
}

interface CategoryChipsProps {
  categories: string[];
  selectedCategory: string | null;
  onSelectCategory: (category: string | null) => void;
}

export function CategoryChips({ categories, selectedCategory, onSelectCategory }: CategoryChipsProps) {
  if (categories.length === 0) return null;

  return (
    <div className="container mx-auto px-4 pt-3">
      {/* Full-bleed, and it swallows the wrapper's top padding: the pager only
          yields a drag that starts on a horizontal scroller, so padding around
          this strip is where a sideways swipe jumps tabs instead of scrolling
          the categories. Negative margins plus matching padding widen the touch
          target without moving a pixel. */}
      <div className="-mx-4 -mt-3 flex gap-3 overflow-x-auto px-4 pb-2 pt-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <CategoryChip
          label="All"
          active={selectedCategory === null}
          onClick={() => onSelectCategory(null)}
          icon={null}
        />
        {categories.map((cat) => {
          const meta = lookupMeta(cat);
          return (
            <CategoryChip
              key={cat}
              label={meta.label}
              active={selectedCategory === cat}
              onClick={() => onSelectCategory(cat)}
              icon={meta.icon}
            />
          );
        })}
      </div>
    </div>
  );
}

interface CategoryChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
  icon: LucideIcon | null;
}

function CategoryChip({ label, active, onClick, icon: Icon }: CategoryChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex shrink-0 flex-col items-center gap-1.5"
      data-testid={`chip-category-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span
        className={`flex h-14 w-14 items-center justify-center rounded-2xl transition ${
          active
            ? "bg-primary text-primary-foreground shadow-sm"
            : "bg-muted text-foreground hover:bg-muted/70"
        }`}
      >
        {Icon ? (
          <Icon className="h-6 w-6" strokeWidth={1.75} />
        ) : (
          <span className="text-sm font-semibold">All</span>
        )}
      </span>
      <span
        className={`text-[11px] font-medium leading-none transition ${
          active ? "text-primary" : "text-muted-foreground"
        }`}
      >
        {label}
      </span>
    </button>
  );
}
